import { ArrowRight, Check, MapPin } from 'lucide-react'
import { Link } from 'react-router-dom'
import HeroSlideshow, { type HeroSlide } from '../components/HeroSlideshow'
import ProfessionalDirectory from '../components/ProfessionalDirectory'
import SEO from '../components/SEO'
import { localBusinessSchema } from '../config/schema'
import { amenities, brand, services } from '../data/site'
import { imageSize } from '../data/images'

const asset = (name: string) => `${import.meta.env.BASE_URL}images/${name}`

const heroSlides: HeroSlide[] = [
  {
    image: 'Home-banner-slider.webp',
    alt: 'Diamond Suites Ocala reception lounge with black seating and equestrian artwork',
    label: 'Reception lounge',
    position: '52% center',
  },
  {
    image: 'directory-banner.webp',
    alt: 'Shared lounge inside Diamond Suites Ocala with black seating',
    label: 'Shared space',
    position: 'center center',
  },
  {
    image: 'home-banner-slider-01-1.webp',
    alt: 'Bright Diamond Suites Ocala waiting area with black and white seating',
    label: 'Client arrival',
    position: 'center center',
  },
  {
    image: 'community-main.jpg',
    alt: 'Exterior entrance of Diamond Suites Ocala at 7494 SW 60th Avenue',
    label: 'Ocala location',
    position: 'center center',
  },
]

export default function Home() {
  return (
    <>
      <SEO page="home" structuredData={localBusinessSchema} />
      <section className="editorial-hero">
        <div className="editorial-hero__copy">
          <div>
            <p className="eyebrow">Private salon suites · Ocala, Florida</p>
            <h1>Salon suites and beauty professionals in Ocala.</h1>
            <p className="editorial-hero__lede">
              Diamond Suites Ocala brings independent beauty and wellness professionals together
              in one polished, welcoming destination.
            </p>
            <div className="hero-actions">
              <Link className="button" to="/contact/">Join the Diamond Family <ArrowRight aria-hidden="true" /></Link>
              <Link className="text-link" to="/directory/">Meet the professionals <ArrowRight aria-hidden="true" /></Link>
            </div>
          </div>
          <div className="editorial-hero__address">
            <MapPin aria-hidden="true" />
            <span>{brand.addressLine1}<br />{brand.addressLine2}</span>
          </div>
        </div>

        <div className="editorial-hero__visual">
          <HeroSlideshow slides={heroSlides} />
        </div>
      </section>

      <section className="service-ribbon" aria-label="Services represented at Diamond Suites Ocala">
        <div className="container service-ribbon__inner">
          <p>Beauty and wellness, under one roof</p>
          <div>
            {services.slice(0, 6).map((service) => <span key={service.title}>{service.title}</span>)}
          </div>
        </div>
      </section>

      <section className="section intro-section" data-reveal>
        <div className="container intro-grid">
          <div className="intro-grid__heading">
            <p className="eyebrow section-coordinate"><span>01</span>The Ocala experience</p>
            <h2>A destination for clients. A home base for professionals.</h2>
          </div>
          <div className="intro-grid__copy">
            <p className="lead">
              Eight private suites create room for personal service, focused work, and distinct
              independent businesses—all within a calm, design-forward setting.
            </p>
            <p>
              Clients can discover multiple specialties at one address, while professionals can
              shape a suite around their own craft, brand, schedule, and client experience.
            </p>
            <Link className="text-link" to="/about/">Our story <ArrowRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section className="section suite-system" aria-labelledby="suite-system-title" data-reveal>
        <div className="container suite-system__grid">
          <div className="suite-system__statement">
            <p className="eyebrow section-coordinate"><span>02</span>The suite system</p>
            <h2 id="suite-system-title">Your space. Your clients. Your business.</h2>
            <p>
              Eight private suites give independent professionals room to shape their work and
              client experience within one shared Ocala destination.
            </p>
            <Link className="button button--light" to="/suites/">Explore the suites <ArrowRight aria-hidden="true" /></Link>
          </div>

          <div className="suite-index" aria-label="The Diamond Suites experience">
            <article>
              <span>01 / YOUR SPACE</span>
              <h3>Private and customizable.</h3>
              <p>Individual suites offer privacy, natural light, secure access, and room to reflect a professional’s own brand.</p>
            </article>
            <article>
              <span>02 / YOUR CLIENTS</span>
              <h3>A polished arrival.</h3>
              <p>A shared waiting area, coffee bar, parking, and common spaces support the visit from the moment clients arrive.</p>
            </article>
            <article>
              <span>03 / YOUR BUSINESS</span>
              <h3>Independently operated.</h3>
              <p>Each professional manages their own services, schedule, appointments, and client relationships.</p>
            </article>
          </div>

          <div className="service-ledger">
            <p>Services represented</p>
            <div>{services.map((service, index) => <span key={service.title}>{String(index + 1).padStart(2, '0')} {service.title}</span>)}</div>
          </div>
        </div>
      </section>

      <section className="availability-bridge" aria-labelledby="availability-title" data-reveal>
        <div className="container availability-bridge__inner">
          <div>
            <p className="eyebrow">Future availability</p>
            <h2 id="availability-title">Suites are currently full, but the next opening could be yours.</h2>
          </div>
          <Link className="text-link" to="/contact/">Join the professional waitlist <ArrowRight aria-hidden="true" /></Link>
        </div>
      </section>

      <section className="section suite-experience suite-experience--connected" data-reveal>
        <div className="container suite-experience__grid">
          <div className="suite-collage">
            <figure className="suite-collage__primary">
              <img src={asset('suite-interior.jpg')} alt="Private hair suite with styling chair and wash basin" {...imageSize('suite-interior.jpg')} loading="lazy" />
            </figure>
            <figure className="suite-collage__secondary">
              <img src={asset('directory-banner.webp')} alt="Shared lounge inside Diamond Suites Ocala" {...imageSize('directory-banner.webp')} loading="lazy" />
            </figure>
          </div>

          <div className="suite-experience__copy">
            <p className="eyebrow section-coordinate"><span>03</span>Space with intention</p>
            <h2>Elevate your business in a space designed around your work.</h2>
            <p>
              Private suites offer focus and flexibility. Shared conveniences support a polished
              experience from the moment a client arrives.
            </p>
            <ul className="amenity-list">
              {amenities.map((amenity) => (
                <li key={amenity}><Check aria-hidden="true" /> {amenity}</li>
              ))}
            </ul>
            <Link className="button button--outline" to="/suites/">Explore the suites <ArrowRight aria-hidden="true" /></Link>
          </div>
        </div>
      </section>

      <section className="section professionals-section" data-reveal>
        <div className="container">
          <div className="section-heading section-heading--row">
            <div>
              <p className="eyebrow section-coordinate"><span>04</span>Inside the suites</p>
              <h2>Meet your next go-to professional.</h2>
            </div>
            <div>
              <p>Each professional operates an independent business and manages their own services, schedule, and appointments.</p>
              <Link className="text-link" to="/directory/">View the full directory <ArrowRight aria-hidden="true" /></Link>
            </div>
          </div>
          <ProfessionalDirectory preview />
        </div>
      </section>

      <section className="section join-section" data-reveal>
        <div className="container join-section__grid">
          <div className="join-section__copy">
            <p className="eyebrow section-coordinate"><span>05</span>For beauty and wellness professionals</p>
            <h2>Bring your business into the Diamond family.</h2>
            <p>
              The team welcomes inquiries from independent professionals interested in future
              opportunities and a polished home for their business.
            </p>
            <Link className="button button--light" to="/contact/">Join the waitlist <ArrowRight aria-hidden="true" /></Link>
          </div>
          <ol className="join-steps">
            <li><span>01</span><div><h3>Introduce your business</h3><p>Share your specialty, experience, and what you are looking for in a suite.</p></div></li>
            <li><span>02</span><div><h3>Connect with the team</h3><p>Ask questions and learn about the space, amenities, and pre-approval process.</p></div></li>
            <li><span>03</span><div><h3>Plan for an opening</h3><p>Qualified professionals can be considered when a suitable suite becomes available.</p></div></li>
          </ol>
        </div>
      </section>

      <section className="section gallery-section" data-reveal>
        <div className="container">
          <div className="section-heading">
            <p className="eyebrow section-coordinate"><span>06</span>A look inside</p>
            <h2>Clean lines. Private spaces. A distinctly Ocala point of view.</h2>
          </div>
          <div className="gallery-grid">
            <figure className="gallery-grid__wide">
              <img src={asset('directory-banner.webp')} alt="Diamond Suites Ocala lounge with equestrian artwork" {...imageSize('directory-banner.webp')} loading="lazy" />
            </figure>
            <figure className="gallery-grid__tall">
              <img src={asset('suite-interior.jpg')} alt="Private suite with black salon equipment" {...imageSize('suite-interior.jpg')} loading="lazy" />
            </figure>
            <figure>
              <img src={asset('home-banner-slider-01-1.webp')} alt="Bright waiting area with black and white seating" {...imageSize('home-banner-slider-01-1.webp')} loading="lazy" />
            </figure>
            <figure>
              <img src={asset('community-main.jpg')} alt="Exterior of Diamond Suites Ocala at 7494 SW 60th Avenue" {...imageSize('community-main.jpg')} loading="lazy" />
            </figure>
          </div>
        </div>
      </section>

      <section className="section location-section" data-reveal>
        <div className="container location-grid">
          <div className="location-grid__image">
            <img src={asset('community-main.jpg')} alt="Front entrance of Diamond Suites Ocala" {...imageSize('community-main.jpg')} loading="lazy" />
          </div>
          <div className="location-grid__copy">
            <p className="eyebrow section-coordinate"><span>07</span>Find us in Ocala</p>
            <h2>One address. A full spectrum of care.</h2>
            <address>
              {brand.addressLine1}<br />
              {brand.addressLine2}
            </address>
            <a href={brand.phoneHref}>{brand.phoneDisplay}</a>
            <a href={`mailto:${brand.email}`}>{brand.email}</a>
            <div className="location-grid__actions">
              <a className="button" href={brand.mapUrl} target="_blank" rel="noopener noreferrer">Get directions to Diamond Suites Ocala <ArrowRight aria-hidden="true" /></a>
              <Link className="text-link" to="/contact/">Contact the team <ArrowRight aria-hidden="true" /></Link>
            </div>
          </div>
        </div>
      </section>

      <section className="final-cta">
        <div className="container final-cta__inner">
          <p className="eyebrow">Your next chapter</p>
          <h2>Ready to make your business part of Diamond Suites Ocala?</h2>
          <Link className="button button--light" to="/contact/">Start an inquiry <ArrowRight aria-hidden="true" /></Link>
        </div>
      </section>
    </>
  )
}
